import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { IconButton, Icon } from '@cbrebuild/blocks';
import _ from 'lodash';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import VOUCHER_ROUTES from '../../../page-templates/voucher/voucher-subroutes';
import RequiredLabeledItem from './RequiredLabeledItem';
import LabeledItem from '../../../nucleus/labeled-item/labeled-item';
import formatAsCurrency from '../../../utils/format-as-currency';
import Percent from '../../../nucleus/formats/percent';
import { PAYMENT_GROUPS } from '../../../services/voucher/commissions-service';
import { splitPropertyTypeAndSubtype } from '../../../utils/property-type-utils';
import { sortConsiderationsByPositionAndId } from '../../../utils/consideration-term-utils';
import { isNumericOutOfRange, formatOutOfRangeMessage } from '../../../utils/broker-verification-utils';

// Average days-per-month used to convert a lease's actual day-span into a whole number of
// months, matching the pre-broker-verification `getLeaseDuration` this reinstates.
const AVG_DAYS_PER_MONTH = 365 / 12;

const parseBooleanValue = value => value === true || String(value).toLowerCase() === 'true';

// Same choices as the live Comp Data tab's "Lease Escalation" Select
// (CompDataTab/LeaseComp.js's `leaseEscalationOptions`) — the underlying field stores a
// boolean-ish value, compared as a string on the live tab too (`.toString()`).
const leaseEscalationTypeOptions = [
  { value: 'false', display_name: 'Fixed' },
  { value: 'true', display_name: 'Custom' },
];

// Same choices as the live Comp Data tab's "Tenant Improvements Detail" Select
// (CompDataTab/LeaseComp.js's `tenantImprovementDetailOptions`).
const tenantImprovementDetailOptions = [
  { value: 'Allowance', display_name: 'Allowance' },
  { value: 'As Is', display_name: 'As Is' },
  { value: 'Spec Suite', display_name: 'Spec Suite' },
  { value: 'Turn Key', display_name: 'Turn Key' },
];

const CompDataReview = ({ fieldBounds, baseRent }) => {
  const {
    cbreCommissions, compData, commercialProperties, considerations, deal, outsideCommissions, voucher,
  } = useSelector(state => ({
    cbreCommissions: state.commissions.commissions?.filter(({ payment_group }) => payment_group === PAYMENT_GROUPS.CBRE_BROKER),
    compData: state.compData,
    commercialProperties: state.voucher.commercialProperties,
    considerations: state.voucherConsiderations?.considerations,
    deal: state.deal.deal,
    outsideCommissions: state.commissions.commissions?.filter(({ payment_group }) => payment_group === PAYMENT_GROUPS.OUTSIDE_BROKER),
    voucher: state.voucher.voucher,
  }));
  const [compDataIsLoaded, setCompDataIsLoaded] = useState(false);
  useEffect(() => {
    if (!compData.compData) setCompDataIsLoaded(true);
  }, [compData]);
  const history = useHistory();
  const url = VOUCHER_ROUTES[3].path.replace(':id', voucher.deal);
  const requiredText = ({ notRequired = false } = {}) => {
    if (notRequired) return '--';
    localStorage.setItem('voucher_valid', JSON.stringify(false));
    return <span className="voucher-error-text">Required</span>;
  };
  const isDealType = args => !!_.find(args, arg => arg === deal.deal_type);
  const tenantBuyer = (deal.rep_role === 'tenant' || deal.rep_role === 'buyer') ? deal.client : deal.counter_party;
  const landlordSeller = (deal.rep_role === 'landlord' || deal.rep_role === 'seller') ? deal.client : deal.counter_party;
  const properties = _.sortBy(commercialProperties, 'id').filter(property => property.location_type_new === true);
  const getSquareFoot = (property) => {
    if (property) {
      return property.whole_building
        ? parseInt(property.building_total_square_feet, 10)
        : _.sumBy(property.spaces, o => o.space_total_square_feet);
    }
    return null;
  };
  // Reuses `getSquareFoot` (rather than re-deriving the same whole_building/spaces logic) for
  // the aggregate square footage used by `getPricePerSqFt()` below.
  const totalPropertySqft = _.sumBy(properties, getSquareFoot);
  const previousProperty = _.filter(commercialProperties, property => property.location_type_new === false)[0];
  // `baseRent` comes from the API's separate market-based Algorithm 1 resolution (see
  // broker_verification_services.py) -- it's returned as its own top-level `{min, max,
  // usedDefault, market}` object (deliberately excluded from `fieldBounds`, which only
  // covers Algorithm 2's generic Numeric/Date rows), so it needs a `category: 'NUMERIC'`
  // added here to match the shape `isNumericOutOfRange`/`RequiredLabeledItem` expect.
  const baseRentBounds = baseRent ? { category: 'NUMERIC', min: baseRent.min, max: baseRent.max } : null;
  // Rick confirmed (2026-07-30) Lease Term validation must be the ACTUAL lease duration --
  // the range between the lease's Commencement and Expiration dates -- not a separate
  // Requirements-page estimate. There is no dedicated Lease Term UI field anywhere in
  // DealIQ, so this derives it from the two dates already shown directly above it and
  // validates the result against fieldBounds.term; it is display-only (not a
  // RequiredLabeledItem/panel field) since there's no single real field to edit.
  // Uses voucher.lease_expiration_date (not deal.date_lease_to) because the Lease
  // Information tab writes the expiration date to the voucher via updateVoucher --
  // deal.date_lease_to stays null until the voucher process syncs it later, so it's
  // never populated yet while the voucher is still in progress (i.e. here in Review).
  const getLeaseDuration = () => {
    if (!deal.date_lease_from || !voucher.lease_expiration_date) return null;
    const leaseStart = moment(deal.date_lease_from);
    const leaseEnd = moment(voucher.lease_expiration_date);
    const leaseTermDays = leaseEnd.diff(leaseStart, 'days');
    return Math.round(leaseTermDays / AVG_DAYS_PER_MONTH);
  };
  const returnMonthRent = (months) => {
    if (months === 0) return '0';
    if (months) return parseFloat((months % 12).toFixed(4));
    return null;
  };
  const returnYearRent = (months) => {
    if (months === 0) return '0';
    if (months) return Math.floor(months / 12);
    return null;
  };
  const purchasePrice = _.sumBy(considerations, o => ((o.consideration_type === 'Purchase Price') ? o.amount : 0));
  const getPricePerSqFt = () => (totalPropertySqft ? (purchasePrice / totalPropertySqft) : null);
  const rentEscalationIsCustom = parseBooleanValue(compData?.rent_escalation_is_custom);
  // Neither the Fixed nor the Custom field should appear until the user has actually picked
  // an option from the Lease Escalation dropdown -- `rent_escalation_is_custom` is null
  // before that, and `parseBooleanValue(null)` resolves to `false`, which would otherwise
  // make the Fixed field show by default.
  const rentEscalationTypeSelected = compData?.rent_escalation_is_custom === true || compData?.rent_escalation_is_custom === false;
  // A row only counts as "complete" once it has BOTH a rent value (dollar or percent) and a
  // months value -- mirrors `customLeaseEscalationField.js`'s own `rentIsEmpty`/
  // `isMonthsEmpty` checks, which drive its "Required"/"Year is required"/"Month is required"
  // messages. Drives `invalid` below so the schedule stays flagged in "Before you submit"
  // even though the array itself is non-empty (an incomplete row wouldn't otherwise fail
  // RequiredLabeledItem's own empty-array check).
  const isBlankValue = v => v === null || v === undefined || v === '';
  const escalationScheduleHasIncompleteRow = Array.isArray(compData?.rent_escalation_custom) &&
    compData.rent_escalation_custom.some(row => (
      (isBlankValue(row.rent_dollar) && isBlankValue(row.rent_percent)) || isBlankValue(row.months)
    ));
  // "Lease Escalation" and its Fixed/Custom detail field below are one logical field, not two
  // -- mirrors GettingStartedReview.js's "Account Client"/"Client Code - Description" pairing.
  // Forces "Lease Escalation" into the panel even once it's picked (no error of its own) so it
  // doesn't stay hidden while its own detail field still shows as required, and (combined with
  // `SECTION_FIELD_PRIORITY['Comp Data']` in RequiredFieldsSummary.js) keeps both rows adjacent
  // instead of the detail field landing at the end of the section based on when it happened to
  // start needing attention (only once Fixed/Custom is picked, well after other fields already
  // registered).
  const escalationDetailMissing = rentEscalationTypeSelected && !voucher.is_early_invoice && (
    rentEscalationIsCustom
      ? (!compData?.rent_escalation_custom?.length || escalationScheduleHasIncompleteRow)
      : isBlankValue(compData?.rent_escalation_is_percent ? compData?.rent_escalation_percent : compData?.rent_escalation_dollar)
  );
  // Snapshots, once per voucher visit, whether the Fixed/Custom detail field was already the
  // ACTIVE type AND complete the first moment compData loads -- mirrors GettingStartedReview.js's
  // commission-agreement-attachment fix. Both detail fields below unmount/remount every time
  // "Lease Escalation" is toggled between Fixed and Custom, which would otherwise wipe out their
  // own `everNeededAttentionRef` latch and make an already-resolved field (edited during this
  // visit, or switched TO during this visit) vanish instead of staying visible with a checkmark.
  // Requiring the type to also match what was loaded matters for Custom specifically: its old
  // schedule rows are never cleared server-side when the live tab switches to Fixed, so loading
  // the page on Fixed (with stale-but-complete Custom data sitting unused underneath) must NOT
  // count as "already complete" -- toggling to Custom is a fresh action this visit and the
  // schedule (with its own "Add Rent" row-adding affordance) should surface for review either way.
  const escalationFixedAlreadyCompleteOnLoadRef = useRef(null);
  const escalationCustomAlreadyCompleteOnLoadRef = useRef(null);
  useEffect(() => {
    escalationFixedAlreadyCompleteOnLoadRef.current = null;
    escalationCustomAlreadyCompleteOnLoadRef.current = null;
  }, [voucher.id]);
  useEffect(() => {
    if (!compDataIsLoaded) return;
    if (escalationFixedAlreadyCompleteOnLoadRef.current === null) {
      escalationFixedAlreadyCompleteOnLoadRef.current = compData?.rent_escalation_is_custom === false
        && !isBlankValue(compData?.rent_escalation_is_percent ? compData?.rent_escalation_percent : compData?.rent_escalation_dollar);
    }
    if (escalationCustomAlreadyCompleteOnLoadRef.current === null) {
      escalationCustomAlreadyCompleteOnLoadRef.current = compData?.rent_escalation_is_custom === true
        && !!compData?.rent_escalation_custom?.length && !escalationScheduleHasIncompleteRow;
    }
  }, [compDataIsLoaded]);
  const escalationFixedForceRegister = (!rentEscalationIsCustom && escalationDetailMissing)
    || escalationFixedAlreadyCompleteOnLoadRef.current === false;
  const escalationCustomForceRegister = (rentEscalationIsCustom && escalationDetailMissing)
    || escalationCustomAlreadyCompleteOnLoadRef.current === false;
  const renderEscalationRent = (item) => {
    const isPercent = item.is_percent === true || String(item.is_percent).toLowerCase() === 'true';
    if (!isPercent) {
      return item.rent_dollar != null && item.rent_dollar !== ''
        ? formatAsCurrency(item.rent_dollar)
        : requiredText({ notRequired: voucher.is_early_invoice });
    }
    return item.rent_percent != null && item.rent_percent !== ''
      ? <Percent value={parseFloat(item.rent_percent)} decimalRounding={2} />
      : requiredText({ notRequired: voucher.is_early_invoice });
  };

  const displayEscalationSchedule = rentEscalationIsCustom ? (
    <table>
      <colgroup>
        <col />
        <col name="rent" />
        <col name="years" />
        <col name="months" />
      </colgroup>
      <tbody>
        <tr className="borderless-row">
          <th />
          <th>Rent</th>
          <th>Years</th>
          <th>Months</th>
        </tr>
        {compData?.rent_escalation_custom?.map((item, index) => {
          const rowKey = `${item.months}-${item.rent_dollar}-${item.rent_percent}-${item.is_percent}`;
          return (
            <tr key={rowKey}>
              <td>{index + 1}</td>
              <td>{renderEscalationRent(item)}</td>
              <td>{item.months > 0
                ? returnYearRent(item.months)
                : (index === 0 && requiredText({ notRequired: voucher.is_early_invoice })) || '--'}
              </td>
              <td>{item.months > 0
                ? returnMonthRent(item.months)
                : (index === 0 && requiredText({ notRequired: voucher.is_early_invoice })) || '--'}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  ) : null;
  const displayRentSchedule = (
    <table>
      <colgroup>
        <col />
        <col name="rent type" />
        <col name="period" />
        <col name="rent" />
      </colgroup>
      <tbody>
        <tr className="borderless-row">
          <th>Rent Type</th>
          <th>Period</th>
          <th>Rent</th>
        </tr>
        {sortConsiderationsByPositionAndId(considerations).map(item => (
          <tr key={item.id}>
            <td>{item.consideration_type}</td>
            <td>{item.total_units}</td>
            <td>{formatAsCurrency(item.amount)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const sortedCBREBrokers = cbreCommissions.sort((a, b) => {
    if (a.broker_is_lead) return -1;
    if (b.broker_is_lead) return 1;
    return 0;
  });
  const sortedOutsideBrokers = outsideCommissions.sort((a, b) => {
    if (a.broker_is_lead) return -1;
    if (b.broker_is_lead) return 1;
    return 0;
  });

  return (
    <div className="voucher-review-group comp-data-review no-divider">
      <div className="navigation">
        <Link className="h2 link" to={url}>Comp Data</Link>
        <IconButton iconName="edit" onClick={() => { history.push(url); }} />
      </div>
      <div className="content">
        <div className="voucher-review-group no-divider">
          <h3>General Information</h3>
          <div className="two-cols">
            <div className="col-left">
              <RequiredLabeledItem label="Transaction Type" item={deal.deal_type === 'sale' ? 'Sale' : 'Lease'} notRequired isReview={compDataIsLoaded} />
              {isDealType(['lease']) ?
                <>
                  <RequiredLabeledItem label="Tenant" item={tenantBuyer?.name} notRequired isReview={compDataIsLoaded} />
                  <RequiredLabeledItem label="Tenant Industry" item={tenantBuyer?.voucher_naics_category} notRequired isReview={compDataIsLoaded} />
                  <RequiredLabeledItem
                    label="Tenant Represented By"
                    item={compData?.tenant_represented_by}
                    notRequired={voucher.is_early_invoice}
                    isReview={compDataIsLoaded}
                    fieldId="comp-data-tenant-represented-by"
                    section="Comp Data"
                    editTarget={{ reduxAction: 'updateCompData', fieldPath: 'tenant_represented_by' }}
                  />
                  <RequiredLabeledItem
                    label="Tenant Represented By Agent"
                    item={compData?.tenant_represented_by_agent}
                    notRequired={voucher.is_early_invoice}
                    isReview={compDataIsLoaded}
                    fieldId="comp-data-tenant-represented-by-agent"
                    section="Comp Data"
                    editTarget={{ reduxAction: 'updateCompData', fieldPath: 'tenant_represented_by_agent' }}
                  />
                  <RequiredLabeledItem label="Agile Deal" item={deal.is_agile_deal} format="bool" notRequired isReview={compDataIsLoaded} />
                </>
                : ''
              }
              {isDealType(['sale']) ?
                <>
                  <RequiredLabeledItem label="Buyer" item={tenantBuyer?.name} notRequired isReview={compDataIsLoaded} />
                  <RequiredLabeledItem label="Buyer Industry" item={tenantBuyer?.voucher_naics_category} notRequired isReview={compDataIsLoaded} />
                  <RequiredLabeledItem
                    label="Buyer Represented By"
                    item={compData?.buyer_represented_by}
                    notRequired={voucher.is_early_invoice}
                    isReview={compDataIsLoaded}
                    fieldId="comp-data-buyer-represented-by"
                    section="Comp Data"
                    editTarget={{ reduxAction: 'updateCompData', fieldPath: 'buyer_represented_by' }}
                  />
                  <RequiredLabeledItem
                    label="Buyer Represented By Agent"
                    item={compData?.buyer_represented_by_agent}
                    notRequired={voucher.is_early_invoice}
                    isReview={compDataIsLoaded}
                    fieldId="comp-data-buyer-represented-by-agent"
                    section="Comp Data"
                    editTarget={{ reduxAction: 'updateCompData', fieldPath: 'buyer_represented_by_agent' }}
                  />
                </>
                : ''
              }
            </div>
            <div className="col-right">
              <RequiredLabeledItem label="Account Driven" item={compData?.account_driven_deal} notRequired isReview={compDataIsLoaded} />
              {isDealType(['lease']) ?
                <>
                  <RequiredLabeledItem label="Landlord" item={landlordSeller?.name} notRequired isReview={compDataIsLoaded} />
                  <RequiredLabeledItem
                    label="Landlord Represented By"
                    item={compData?.landlord_represented_by}
                    notRequired={voucher.is_early_invoice}
                    isReview={compDataIsLoaded}
                    fieldId="comp-data-landlord-represented-by"
                    section="Comp Data"
                    editTarget={{ reduxAction: 'updateCompData', fieldPath: 'landlord_represented_by' }}
                  />
                  <RequiredLabeledItem
                    label="Landlord Represented By Agent"
                    item={compData?.landlord_represented_by_agent}
                    notRequired={voucher.is_early_invoice}
                    isReview={compDataIsLoaded}
                    fieldId="comp-data-landlord-represented-by-agent"
                    section="Comp Data"
                    editTarget={{ reduxAction: 'updateCompData', fieldPath: 'landlord_represented_by_agent' }}
                  />
                </>
                : ''
              }
              {isDealType(['sale']) ?
                <>
                  <RequiredLabeledItem label="Seller" item={landlordSeller?.name} notRequired isReview={compDataIsLoaded} />
                  <RequiredLabeledItem
                    label="Seller Represented By"
                    item={compData?.seller_represented_by}
                    notRequired={voucher.is_early_invoice}
                    isReview={compDataIsLoaded}
                    fieldId="comp-data-seller-represented-by"
                    section="Comp Data"
                    editTarget={{ reduxAction: 'updateCompData', fieldPath: 'seller_represented_by' }}
                  />
                  <RequiredLabeledItem
                    label="Seller Represented By Agent"
                    item={compData?.seller_represented_by_agent}
                    notRequired={voucher.is_early_invoice}
                    isReview={compDataIsLoaded}
                    fieldId="comp-data-seller-represented-by-agent"
                    section="Comp Data"
                    editTarget={{ reduxAction: 'updateCompData', fieldPath: 'seller_represented_by_agent' }}
                  />
                </>
                : ''
              }
              {(isDealType(['lease']) && deal.is_agile_deal) ?
                <>
                  <RequiredLabeledItem label="Cost Per Seat" item={deal.cost_per_seat} format="currency" notRequired isReview={compDataIsLoaded} />
                  <RequiredLabeledItem label="Number of Seats" item={deal.number_of_seats} format="number" notRequired isReview={compDataIsLoaded} />
                </>
                : ''
              }
            </div>
          </div>
        </div>
        {isDealType(['lease']) &&
          <div className="voucher-review-group no-divider">
            <h3>Lease Details</h3>
            <div className="two-cols">
              <div className="col-left">
                <RequiredLabeledItem label="Prior Location" item={previousProperty} format="address" notRequired isReview={compDataIsLoaded} />
                <RequiredLabeledItem label="Prior SF" item={getSquareFoot(previousProperty)} format="number" notRequired isReview={compDataIsLoaded} />
              </div>
              <div className="col-right">
                <RequiredLabeledItem label="Rate Type" item={deal.lease_agreement_type} notRequired isReview={compDataIsLoaded} />
                <RequiredLabeledItem label="Lease Commencement Date" item={deal.date_lease_from} format="shortDate" notRequired isReview={compDataIsLoaded} />
                <RequiredLabeledItem
                  label="Lease Expiration Date"
                  item={voucher.lease_expiration_date}
                  format="shortDate"
                  notRequired
                  isReview={compDataIsLoaded}
                />
                {(() => {
                  const leaseTermMonths = getLeaseDuration();
                  const leaseTermOutOfRange = isNumericOutOfRange(leaseTermMonths, fieldBounds.term);
                  return (
                    <LabeledItem
                      label="Lease Term (months)"
                      item={leaseTermMonths}
                      emptyState="--"
                      itemClassName={leaseTermOutOfRange ? 'item--warning' : ''}
                      labelIcon={leaseTermOutOfRange && (
                        <Icon iconName="warning" variant="warning" size="medium" className="required-labeled-item__warning-icon" />
                      )}
                      message={leaseTermOutOfRange && (
                        <>
                          {formatOutOfRangeMessage(fieldBounds.term, 'number', 'months')}
                          <br />
                          Please review and adjust your lease start date and expiration date.
                        </>
                      )}
                    />
                  );
                })()}
                <RequiredLabeledItem label="Lease Sign Date" item={deal.date_lease_execution} format="shortDate" notRequired isReview={compDataIsLoaded} />
                <RequiredLabeledItem label="Lease Occupancy Date" item={deal.date_lease_occupancy} format="shortDate" notRequired isReview={compDataIsLoaded} />
              </div>
            </div>
          </div>}
        <div className="voucher-review-group no-divider">
          {properties?.map((property, index) => (
            <div key={property.id || index}>
              <h3>Property #{index + 1}</h3>
              <div className="two-cols">
                <div className="left-col">
                  <RequiredLabeledItem label="Property Name" item={property?.name} notRequired isReview={compDataIsLoaded} />
                  <RequiredLabeledItem label="Property Address" item={property} format="address" notRequired isReview={compDataIsLoaded} />
                  <RequiredLabeledItem label="Property Type" item={splitPropertyTypeAndSubtype(property?.voucher_property_type_category).type} notRequired isReview={compDataIsLoaded} />
                  <RequiredLabeledItem label="Property Subtype" item={splitPropertyTypeAndSubtype(property?.voucher_property_type_category).subtype} notRequired isReview={compDataIsLoaded} />
                  {property.whole_building &&
                    <>
                      <RequiredLabeledItem label="Square Feet" item={getSquareFoot(property)} format="number" notRequired isReview={compDataIsLoaded} />
                      <RequiredLabeledItem label="Acreage" item={property?.parcel_size} format="number" notRequired isReview={compDataIsLoaded} />
                    </>
                  }
                </div>
                <div className="right-col">
                  {!property.whole_building &&
                      property?.spaces?.map((space, indexSuite) => (
                        <div key={indexSuite}>
                          <h3>Suite #{indexSuite + 1}</h3>
                          <RequiredLabeledItem label="Suite" item={space.address_line2} format="number" notRequired isReview={compDataIsLoaded} />
                          <RequiredLabeledItem label="Floor" item={space.floor} format="number" notRequired isReview={compDataIsLoaded} />
                          <RequiredLabeledItem
                            label="Square Feet"
                            item={space.space_total_square_feet}
                            format="number"
                            notRequired
                            isReview={compDataIsLoaded}
                          />
                        </div>
                      ))}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="voucher-review-group no-divider">
          <h3>Rent &amp; Escalations</h3>
          {isDealType(['lease']) &&
            <div className="two-cols">
              <div className="left-col">
                <RequiredLabeledItem
                  label="Base Rent ($/SF per year)"
                  item={compData?.base_rent}
                  format="currency"
                  notRequired={voucher.is_early_invoice}
                  isReview={compDataIsLoaded}
                  fieldId={voucher.is_early_invoice ? undefined : 'comp-data-base-rent'}
                  section="Comp Data"
                  controlType="currency"
                  editTarget={{ reduxAction: 'updateCompData', fieldPath: 'base_rent' }}
                  bounds={baseRentBounds}
                  rangeFormat="currency"
                  rangeUnit="/sqft per year"
                />
                <RequiredLabeledItem
                  label="Free Rent (Months)"
                  item={compData?.free_rent}
                  format="number"
                  notRequired={voucher.is_early_invoice}
                  isReview={compDataIsLoaded}
                  fieldId={voucher.is_early_invoice ? undefined : 'comp-data-free-rent'}
                  section="Comp Data"
                  controlType="freeRent"
                  editTarget={{ reduxAction: 'updateCompData', fieldPath: 'free_rent' }}
                  bounds={fieldBounds.free_rent}
                  rangeFormat="number"
                  rangeUnit="months"
                />
                <RequiredLabeledItem
                  label="Lease Escalation"
                  item={compData?.rent_escalation_is_custom != null ? String(compData.rent_escalation_is_custom) : null}
                  format="select"
                  notRequired={voucher.is_early_invoice}
                  isReview={compDataIsLoaded}
                  fieldId={voucher.is_early_invoice ? undefined : 'comp-data-lease-escalation-type'}
                  section="Comp Data"
                  controlType="select"
                  options={leaseEscalationTypeOptions}
                  forceRegister={escalationDetailMissing}
                  editTarget={{ reduxAction: 'updateCompData', fieldPath: 'rent_escalation_is_custom' }}
                />
                {/* Fixed: single Escalation (Per Year) $/% field -- matches the live voucher
                    flow's LeaseComp.js / file-upload lease abstraction's rent-and-escalations.js,
                    which likewise only show this field when Lease Escalation is Fixed, not Custom. */}
                {!rentEscalationIsCustom && rentEscalationTypeSelected && (
                  <RequiredLabeledItem
                    label="Escalation (Per Year)"
                    item={compData?.rent_escalation_is_percent ? compData?.rent_escalation_percent : compData?.rent_escalation_dollar}
                    format={compData?.rent_escalation_is_percent ? 'percent3dp' : 'currency'}
                    notRequired={voucher.is_early_invoice}
                    isReview={compDataIsLoaded}
                    fieldId={voucher.is_early_invoice ? undefined : 'comp-data-escalation-per-year'}
                    section="Comp Data"
                    controlType="percentDollar"
                    forceRegister={escalationFixedForceRegister}
                    editTarget={{
                      reduxAction: 'updateCompData',
                      fieldPath: 'rent_escalation_per_year',
                      isPercent: compData?.rent_escalation_is_percent,
                      percentValue: compData?.rent_escalation_percent,
                      dollarValue: compData?.rent_escalation_dollar,
                    }}
                    bounds={compData?.rent_escalation_is_percent ? fieldBounds.lease_escalation_percent : fieldBounds.lease_escalation_amount}
                    rangeFormat={compData?.rent_escalation_is_percent ? 'percent' : 'currency'}
                  />
                )}
                {/* Custom: repeatable Rent/Year/Month schedule rows -- matches the live voucher
                    flow's own "Lease Escalation Breakdown" (Add Rent) editor. Registers for
                    the "Before you submit" panel only -- its own row count is redundant with
                    the "Escalations Schedule" table below, which already shows this on-page. */}
                {rentEscalationIsCustom && rentEscalationTypeSelected && (
                  <RequiredLabeledItem
                    label="Lease Escalation Breakdown"
                    item={compData?.rent_escalation_custom}
                    format="escalationSchedule"
                    invalid={!voucher.is_early_invoice && escalationScheduleHasIncompleteRow}
                    notRequired={voucher.is_early_invoice}
                    isReview={compDataIsLoaded}
                    fieldId={voucher.is_early_invoice ? undefined : 'comp-data-escalation-schedule'}
                    section="Comp Data"
                    controlType="leaseEscalationSchedule"
                    forceRegister={escalationCustomForceRegister}
                    editTarget={{ reduxAction: 'updateCompData', fieldPath: 'rent_escalation_custom' }}
                    hideDisplay
                  />
                )}

                {rentEscalationIsCustom && !!compData?.rent_escalation_custom?.length &&
                  <div className="voucher-review-field">
                    <span className="voucher-review-label">Escalations Schedule</span>
                    <div style={{ marginTop: '8px' }}>{displayEscalationSchedule}</div>
                  </div>
                }
              </div>
              <div className="right-col">
                <div className="voucher-review-field">
                  <span className="voucher-review-label">Rent Schedule</span>
                  <div style={{ marginTop: '12px' }}>{displayRentSchedule}</div>
                </div>
              </div>
            </div>
          }
          {isDealType(['sale']) &&
            <div className="two-cols">
              <div className="left-col">
                <RequiredLabeledItem label="Sale Price" item={purchasePrice} format="currency" notRequired isReview={compDataIsLoaded} />
                <RequiredLabeledItem label="Price Per SF" item={getPricePerSqFt()} format="currency" notRequired isReview={compDataIsLoaded} />
                <RequiredLabeledItem label="Cap Rate" item={properties.length && properties[0].cap_yield_rate} notRequired isReview={compDataIsLoaded} />
              </div>
            </div>
          }
        </div>
        {isDealType(['lease']) &&
          <>
            <div className="voucher-review-group no-divider">
              <h3>Industrial Leases</h3>
              <div className="two-cols">
                <div className="left-col">
                  <RequiredLabeledItem label="Office Area (SF)" item={compData?.office_area} format="number" notRequired isReview={compDataIsLoaded} />
                  <RequiredLabeledItem
                    label="Warehouse Rent ($/SF)"
                    item={compData?.warehouse_rent_amount_per_sqft}
                    format="currency"
                    notRequired
                    isReview={compDataIsLoaded}
                  />
                  <RequiredLabeledItem label="Power (Amps)" item={compData?.power_amps} format="number" notRequired isReview={compDataIsLoaded} />
                  <RequiredLabeledItem label="GL Door Count" item={compData?.gl_door_count} format="number" notRequired isReview={compDataIsLoaded} />
                  <RequiredLabeledItem label="Truckwell Count" item={compData?.truckwell_count} format="number" notRequired isReview={compDataIsLoaded} />
                  <RequiredLabeledItem label="Rail Serviced" item={compData?.rail_serviced} notRequired isReview={compDataIsLoaded} />
                </div>
                <div className="right-col">
                  <RequiredLabeledItem
                    label="Office Rent ($/SF)"
                    item={compData?.office_rent_amount_per_sqft}
                    format="currency"
                    notRequired
                    isReview={compDataIsLoaded}
                  />
                  <RequiredLabeledItem label="Clear Height (Feet)" item={compData?.clear_height} format="number3dp" notRequired isReview={compDataIsLoaded} />
                  <RequiredLabeledItem label="Power (Volts)" item={compData?.power_volts} format="number" notRequired isReview={compDataIsLoaded} />
                  <RequiredLabeledItem label="DH Door Count" item={compData?.dh_door_count} format="number" notRequired isReview={compDataIsLoaded} />
                  <RequiredLabeledItem label="Cross Dock Count" item={compData?.cross_dock_count} format="number" notRequired isReview={compDataIsLoaded} />
                </div>
              </div>
            </div>
            <div className="voucher-review-group no-divider">
              <h3>Expenses</h3>
              <div className="two-cols">
                <div className="left-col">
                  <RequiredLabeledItem
                    label="Tenant Improvement Allowance ($/SF)"
                    item={compData?.tenant_improvement_allowance_amount_per_sqft}
                    format="currency"
                    notRequired={voucher.is_early_invoice}
                    isReview={compDataIsLoaded}
                    fieldId={voucher.is_early_invoice ? undefined : 'comp-data-tenant-improvement-allowance'}
                    section="Comp Data"
                    controlType="currency"
                    editTarget={{ reduxAction: 'updateCompData', fieldPath: 'tenant_improvement_allowance_amount_per_sqft' }}
                    bounds={fieldBounds.tenant_improvement_allowance}
                    rangeFormat="currency"
                    rangeUnit="/SF"
                  />
                  <RequiredLabeledItem
                    label="Tenant Improvements Detail"
                    item={compData?.tenant_improvements_detail}
                    notRequired={voucher.is_early_invoice || compData?.tenant_improvement_allowance_amount_per_sqft === 0}
                    isReview={compDataIsLoaded}
                    fieldId={
                      (voucher.is_early_invoice || compData?.tenant_improvement_allowance_amount_per_sqft === 0)
                        ? undefined : 'comp-data-tenant-improvements-detail'
                    }
                    section="Comp Data"
                    controlType="select"
                    options={tenantImprovementDetailOptions}
                    editTarget={{ reduxAction: 'updateCompData', fieldPath: 'tenant_improvements_detail' }}
                  />
                  <RequiredLabeledItem
                    label="Operating Expenses ($/SF)"
                    item={compData?.operating_expenses_amount_per_sqft}
                    format="currency"
                    notRequired
                    isReview={compDataIsLoaded}
                    fieldId="comp-data-operating-expenses"
                    section="Comp Data"
                    controlType="currency"
                    editTarget={{ reduxAction: 'updateCompData', fieldPath: 'operating_expenses_amount_per_sqft' }}
                    bounds={fieldBounds.operating_expenses}
                    rangeFormat="currency"
                    rangeUnit="/SF"
                  />
                  <RequiredLabeledItem
                    label="Electric included in Oper Exp."
                    item={compData?.electric_included_in_operating_expenses}
                    format="bool"
                    isReview={compDataIsLoaded}
                    fieldId="comp-data-electric-included-in-opex"
                    section="Comp Data"
                    controlType="boolean"
                    editTarget={{ reduxAction: 'updateCompData', fieldPath: 'electric_included_in_operating_expenses' }}
                  />
                  <RequiredLabeledItem
                    label="Electric Expense ($/SF)"
                    item={compData?.electric_expense_amount_per_sqft}
                    format="currency"
                    notRequired={voucher.is_early_invoice || !!compData?.electric_included_in_operating_expenses}
                    isReview={compDataIsLoaded}
                    fieldId={
                      (voucher.is_early_invoice || !!compData?.electric_included_in_operating_expenses)
                        ? undefined : 'comp-data-electric-expense'
                    }
                    section="Comp Data"
                    controlType="currency"
                    editTarget={{ reduxAction: 'updateCompData', fieldPath: 'electric_expense_amount_per_sqft' }}
                  />
                </div>
                <div className="right-col">
                  <RequiredLabeledItem label="Parking Ratio" item={compData?.parking_ratio} format="number3dp" notRequired isReview={compDataIsLoaded} />
                  <RequiredLabeledItem
                    label="Parking Expenses ($/Space)"
                    item={compData?.parking_expenses_amount_per_space}
                    format="currency"
                    notRequired
                    isReview={compDataIsLoaded}
                  />
                  <RequiredLabeledItem
                    label="Taxes ($/SF)"
                    item={compData?.tax_amount}
                    format="currency"
                    notRequired
                    isReview={compDataIsLoaded}
                  />
                </div>
              </div>
            </div>
          </>
        }
        <div className="voucher-review-group no-divider">
          <h3>Brokers</h3>
          <div className="two-cols">
            <div className="left-col">
              {sortedCBREBrokers?.map(broker => (
                <RequiredLabeledItem
                  key={broker.id}
                  label={broker.broker_is_lead ? 'Lead CBRE Broker' : 'Additional CBRE Broker'}
                  item={broker.broker ? `${broker.broker.first_name} ${broker.broker.last_name}` : '--'}
                  isReview={compDataIsLoaded}
                />
              ))}
            </div>
            <div className="right-col">
              {sortedOutsideBrokers?.map(broker => (
                <div key={broker.id}>
                  <RequiredLabeledItem
                    label={broker.broker_is_lead ? 'Lead Outside Broker Company' : 'Additional Outside Broker Company'}
                    item={broker.vendor?.name}
                    isReview={compDataIsLoaded}
                    notRequired
                  />
                  <RequiredLabeledItem
                    label={broker.broker_is_lead ? 'Lead Outside Broker Name' : 'Additional Outside Broker Name'}
                    item={broker.outside_broker_name}
                    isReview={compDataIsLoaded}
                    notRequired
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="voucher-review-group no-divider">
          <RequiredLabeledItem label="General Comments" item={compData?.general_comments} notRequired isReview={compDataIsLoaded} />
        </div>
      </div>
    </div>
  );
};

CompDataReview.propTypes = {
  fieldBounds: PropTypes.objectOf(PropTypes.shape({
    category: PropTypes.string,
    min: PropTypes.number,
    max: PropTypes.number,
    minDate: PropTypes.string,
    maxDate: PropTypes.string,
  })),
  baseRent: PropTypes.shape({
    min: PropTypes.number,
    max: PropTypes.number,
    usedDefault: PropTypes.bool,
    market: PropTypes.string,
  }),
};

CompDataReview.defaultProps = {
  fieldBounds: {},
  baseRent: null,
};

export default CompDataReview;
